import Input from '../Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import {
  emailScheme,
  passwordConfirmScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import { useNotification } from 'src/hooks/useNotification';
import type { ProjectTeamMember } from 'src/services/projectsService';
import { UserNameFields, UserPasswordFields } from 'src/components/UserFormFields/UserFormFields';

export interface TeamMemberFormValues {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password2: string;
}

export const EditTeamMemberForm = ({
  user,
  onSubmit,
}: {
  user?: Pick<ProjectTeamMember['user'], 'email' | 'first_name' | 'last_name'>;
  // Called with only the fields that changed.
  onSubmit: (data: Partial<TeamMemberFormValues>) => void;
}) => {
  const notification = useNotification();
  const {
    handleSubmit,
    control,
    trigger,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      email: user?.email || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      password: '',
      password2: '',
    },
    resolver: yupResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
        first_name: stringScheme({ required: true }).label('First name'),
        last_name: stringScheme({ required: false }).label('Last name'),
        password: passwordScheme({
          required: false,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        password2: passwordConfirmScheme({
          required: false,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Confirm password'),
      }),
    ),
  });

  const submit = (formValues: TeamMemberFormValues) => {
    const filteredData = (Object.keys(dirtyFields) as (keyof TeamMemberFormValues)[]).reduce<
      Partial<TeamMemberFormValues>
    >((acc, key) => {
      if (dirtyFields[key]) {
        acc[key] = formValues[key];
      }
      return acc;
    }, {});

    if (!Object.keys(filteredData).length) {
      notification.info('Nothing changed');
    } else {
      onSubmit(filteredData);
    }
  };

  return (
    <form id="edit-user-form" onSubmit={handleSubmit(submit)}>
      <div className={'input-container'}>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'email'}
              label={'Email'}
              placeholder={'email@email.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              onBlur={() => trigger('email')}
            />
          )}
        />
      </div>
      <UserNameFields control={control} trigger={trigger} />
      <UserPasswordFields control={control} />
    </form>
  );
};
